import { createSignal, For, onMount } from "solid-js";
import { useI18n } from "@/api/i18n";

type LoadingPoint = {
  text: string;
  delay: number;
};

export const LinkedInLoadingState = () => {
  const { t } = useI18n();
  const [visiblePoints, setVisiblePoints] = createSignal<number[]>([]);

  const loadingPoints: LoadingPoint[] = [
    {
      text: t("onboarding.steps.source.linkedin.loading.point1"),
      delay: 0,
    },
    {
      text: t("onboarding.steps.source.linkedin.loading.point2"),
      delay: 400,
    },
    {
      text: t("onboarding.steps.source.linkedin.loading.point3"),
      delay: 800,
    },
    {
      text: t("onboarding.steps.source.linkedin.loading.point4"),
      delay: 1200,
    },
  ];

  onMount(() => {
    // Show points one by one with delay
    loadingPoints.forEach((_, index) => {
      setTimeout(() => {
        setVisiblePoints((prev) => [...prev, index]);
      }, loadingPoints[index].delay);
    });
  });

  return (
    <div class="flex flex-col items-center justify-center min-h-[400px] w-full py-8">
      {/* Icon/Animation */}
      <div class="mb-8 relative">
        <div class="w-20 h-20 rounded-full bg-linear-to-br from-primary/20 to-primary/5 flex items-center justify-center relative">
          {/* Outer ring */}
          <div class="absolute inset-0 rounded-full border-2 border-primary/30 animate-ping" />
          {/* Inner ring */}
          <div class="absolute inset-2 rounded-full border-2 border-primary/50 animate-pulse" />
          {/* Center icon */}
          <div class="relative z-10 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <i class="ph ph-linkedin-logo text-primary text-2xl" />
          </div>
        </div>
      </div>

      {/* Title */}
      <h3 class="text-2xl font-bold text-gray-900 mb-2 text-center">
        {t("onboarding.steps.source.linkedin.loading.title")}
      </h3>

      {/* Description */}
      <p class="text-gray-600 mb-8 text-center max-w-md">
        {t("onboarding.steps.source.linkedin.loading.description")}
      </p>

      {/* Loading Points */}
      <div class="flex flex-col gap-4 w-full max-w-md">
        <For each={loadingPoints}>
          {(point, index) => {
            const isVisible = () => visiblePoints().includes(index());
            return (
              <div
                class="flex items-start gap-3 transition-[opacity,translate] duration-500 ease-out"
                classList={{
                  "opacity-0 -translate-x-4": !isVisible(),
                  "opacity-100 translate-x-0": isVisible(),
                }}
                style={{
                  "transition-delay": `${point.delay}ms`,
                }}
              >
                <div class="shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                  <div
                    class="w-2 h-2 rounded-full bg-primary"
                    classList={{
                      "animate-pulse": isVisible(),
                    }}
                  />
                </div>
                <p class="text-sm text-gray-700 flex-1 leading-relaxed">
                  {point.text}
                </p>
              </div>
            );
          }}
        </For>
      </div>

      {/* Spinner at the bottom */}
      <div class="mt-8 flex items-center gap-2 text-sm text-gray-500">
        <div class="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-primary" />
        <span>{t("onboarding.steps.source.linkedin.loading.wait")}</span>
      </div>
    </div>
  );
};
